import React from 'react';
import PropTypes from 'prop-types';
import { Calendar, TimePickerView } from 'material-ui-pickers';
import theme, { colors } from '@ublend-npm/aulaui-next-web/themes/default';
import { createMuiTheme } from '@material-ui/core';
import AulaUINextProvider from '@ublend-npm/aulaui-next-web/components/Provider/Provider';

import { Container, TimeContainer, Tabs, Tab } from './DateTimePicker.styled';
import YearSelector from './YearSelector';
import Header from './Header';
import * as Views from './views';

const DateTimePickerTheme = createMuiTheme({
  typography: {
    fontFamily: theme.font.family.workSans,
  },
  palette: {
    primary: {
      main: colors.purple,
    },
    text: {
      hint: colors.grey11,
    },
  },
  overrides: {
    MuiPickersCalendarHeader: {
      iconButton: {
        color: colors.designSystem.blackOpacity60,
        '&:disabled': {
          color: colors.designSystem.blackOpacity38,
        },
      },
    },
  },
});

class DateTimePicker extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      openView: Views.CALENDAR,
    };

    this.handleChangeView = this.handleChangeView.bind(this);
    this.handleAMClick = this.handleAMClick.bind(this);
    this.handlePMClick = this.handlePMClick.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleYearChange = this.handleYearChange.bind(this);
    this.handleCalendarChange = this.handleCalendarChange.bind(this);
    this.handleHourChange = this.handleHourChange.bind(this);
    this.handleMinutesChange = this.handleMinutesChange.bind(this);
    this.handleOpenViewChange = this.handleOpenViewChange.bind(this);
  }

  getTabValue() {
    const { openView } = this.state;
    const dateValues = [Views.YEARS, Views.CALENDAR];
    return dateValues.includes(openView) ? 0 : 1;
  }

  handleChangeView(view) {
    this.setState({ openView: view });
  }

  handleAMClick() {
    const { date, onChange } = this.props;
    const hours = date.getHours();
    if (hours >= 12) {
      const newDate = new Date(date);
      newDate.setHours(hours - 12);
      onChange(newDate);
    }
  }

  handlePMClick() {
    const { date, onChange } = this.props;
    const hours = date.getHours();
    if (hours < 12) {
      const newDate = new Date(date);
      newDate.setHours(hours + 12);
      onChange(newDate);
    }
  }

  handleTabChange(_event, value) {
    this.setState({
      openView: value === 0 ? Views.CALENDAR : Views.HOURS,
    });
  }

  handleYearChange(newDate) {
    const { onChange } = this.props;
    onChange(newDate);
    this.setState({ openView: Views.CALENDAR });
  }

  handleCalendarChange(newDate, isFinished) {
    const { onChange } = this.props;
    onChange(newDate);

    if (isFinished) {
      this.setState({ openView: Views.HOURS });
    }
  }

  handleHourChange(newDate) {
    const { onChange, date } = this.props;
    const hours = newDate.getHours();
    if (hours < 12 && date.getHours() >= 12) {
      newDate.setHours(hours + 12);
    }
    onChange(newDate);
    this.setState({ openView: Views.MINUTES });
  }

  handleMinutesChange(newDate) {
    const { onChange } = this.props;
    onChange(newDate);
  }

  handleOpenViewChange(view) {
    this.setState({ openView: view });
  }

  renderHeader() {
    const { date } = this.props;
    const { openView } = this.state;
    return (
      <Header
        date={date}
        openView={openView}
        onViewChange={this.handleChangeView}
        onAMClicked={this.handleAMClick}
        onPMClicked={this.handlePMClick}
      />
    );
  }

  renderTabs() {
    const value = this.getTabValue();
    return (
      <Tabs value={value} onChange={this.handleTabChange}>
        <Tab label="Date" />
        <Tab label="Time" />
      </Tabs>
    );
  }

  renderOpenView() {
    const { openView } = this.state;
    const { date, disablePast, disableFuture } = this.props;
    switch (openView) {
      case Views.YEARS:
        return (
          <YearSelector
            date={date}
            onChange={this.handleYearChange}
            disablePast={disablePast}
            disableFuture={disableFuture}
          />
        );
      case Views.CALENDAR:
        return (
          <Calendar
            date={date}
            onChange={this.handleCalendarChange}
            disablePast={disablePast}
            disableFuture={disableFuture}
          />
        );
      case Views.HOURS:
      case Views.MINUTES:
        return (
          <TimeContainer>
            <TimePickerView
              date={date}
              type={openView}
              onHourChange={this.handleHourChange}
              onMinutesChange={this.handleMinutesChange}
              onSecondsChange={() => {}}
            />
          </TimeContainer>
        );
      default:
        return null;
    }
  }

  render() {
    return (
      <AulaUINextProvider theme={DateTimePickerTheme}>
        <Container>
          {this.renderHeader()}
          {this.renderTabs()}
          {this.renderOpenView()}
        </Container>
      </AulaUINextProvider>
    );
  }
}

DateTimePicker.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  onChange: PropTypes.func.isRequired,
  disablePast: PropTypes.bool,
  disableFuture: PropTypes.bool,
};

DateTimePicker.defaultProps = {
  disablePast: false,
  disableFuture: false,
};

export default DateTimePicker;
